import { useState, useMemo, useCallback, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList, AlertTriangle, Calendar, RefreshCw, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { getUserGreetingName } from "@/lib/profileUtils";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OpenInspectionsPanel } from "@/components/dashboard/openInspections/OpenInspectionsPanel";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import TeamStatistics from "@/components/dashboard/TeamStatistics";
import { PageContainer } from "@/components/layout/PageContainer";
import { ScoreStatistics } from "@/components/dashboard/ScoreStatistics";
import { UserPermissionsCard } from "@/components/dashboard/UserPermissionsCard";
import { SetupValidationWarning, SecretsWarning } from "@/components/admin/SetupValidationWarning";
import { useAssignableProfilesForDefects } from "@/hooks/useAssignableProfiles";
import { useDashboardQuery, DashboardFilters } from "@/hooks/useDashboardQuery";
import {
  getDisplayStatus,
  getDateRangeFromPreset,
  buildDefectsRoute as buildDefectsRouteHelper,
  buildInspectionsRoute as buildInspectionsRouteHelper,
  buildInspectionCards,
  buildDefectCards,
  resolveInspectionChipLabel,
  resolveDefectChipLabel,
  type DatePreset,
} from "@/components/dashboard/dashboardHelpers";
import { ProfileSelect } from "@/components/common/ProfileSelect";
import { StatCard } from "@/components/dashboard/StatCard";
// TES-1633: lazy → eigener Chunk, haelt die analytische KPI-Sektion aus dem eager index.js-Bundle.
const DashboardKpiSection = lazy(() =>
  import("@/components/dashboard/DashboardKpiSection").then((m) => ({
    default: m.DashboardKpiSection,
  })),
);
import { DashboardBento } from "@/components/dashboard/DashboardBento";
import {
  DashboardQuickActions,
  RoleDashboardHeader,
} from "@/components/dashboard/RoleDashboardSections";
import { InspectionCharts, DefectCharts } from "@/components/dashboard/DashboardCharts";
import { TeamFilterDropdown } from "@/components/team/TeamFilterDropdown";
import { useActiveTeam } from "@/hooks/useActiveTeam";
import { resolveTeamScopedFilter } from "@/lib/teamScope";

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : de;
  const { user, profile, isAdmin, isQualitaetsmanager, isTester, isMangelbearbeiter } = useAuth();
  const canSeeAll = isAdmin || isQualitaetsmanager;

  // Mangelbearbeiter sieht keine Prüfungen - nur wenn er AUSSCHLIESSLICH diese Rolle hat
  const showInspections = !isMangelbearbeiter || isTester || isQualitaetsmanager || isAdmin;

  // Pie chart view toggle
  const [showDetailedView, setShowDetailedView] = useState(false);

  // Filter states - default to 'all' (no date filter)
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [inspectionStatusFilter, setInspectionStatusFilter] = useState<string>("all");
  const [defectStatusFilter, setDefectStatusFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [assignedToFilter, setAssignedToFilter] = useState<string>("all");
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const { activeTeamId, isLoading: isActiveTeamLoading } = useActiveTeam();
  const effectiveTeamFilter = resolveTeamScopedFilter(teamFilter, activeTeamId);

  // Fetch assignable profiles for the filter using cached hook
  const { data: assignableProfiles = [] } = useAssignableProfilesForDefects();

  // Build filters for query
  const filters: DashboardFilters = useMemo(
    () => ({
      dateFrom: dateRange?.from?.toISOString(),
      dateTo: dateRange?.to?.toISOString() || dateRange?.from?.toISOString(),
      inspectionStatusFilter,
      defectStatusFilter,
      severityFilter,
      assignedToFilter,
      teamId: effectiveTeamFilter,
    }),
    [
      dateRange,
      inspectionStatusFilter,
      defectStatusFilter,
      severityFilter,
      assignedToFilter,
      effectiveTeamFilter,
    ],
  );

  // Use the React Query hook for all dashboard data
  const {
    stats,
    recentInspections,
    criticalDefects,
    inspectionTrend,
    defectTrend,
    inspectionsByStatus,
    inspectionsSimple,
    defectsByStatus,
    defectsSimple,
    defectsBySeverity,
    isLoading: isDashboardLoading,
    isFetching,
    refetch,
  } = useDashboardQuery(user?.id, canSeeAll, filters, {
    isAdmin,
    isQualitaetsmanager,
    isTester,
    language: i18n.language,
    t,
    enabled: !isActiveTeamLoading,
  });
  const isLoading = isActiveTeamLoading || isDashboardLoading;

  // Check if any filters are active
  const hasActiveFilters =
    datePreset !== "all" ||
    inspectionStatusFilter !== "all" ||
    defectStatusFilter !== "all" ||
    severityFilter !== "all" ||
    assignedToFilter !== "all" ||
    teamFilter !== null;

  // Clear all filters
  const clearAllFilters = () => {
    setDatePreset("all");
    setDateRange(undefined);
    setInspectionStatusFilter("all");
    setDefectStatusFilter("all");
    setSeverityFilter("all");
    setAssignedToFilter("all");
    setTeamFilter(null);
  };

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange(getDateRangeFromPreset(preset, dateLocale, dateRange));
    }
  };

  // Build dynamic route for defects navigation with all active filters
  const buildDefectsRoute = useCallback(
    (status?: string, severity?: string): string =>
      buildDefectsRouteHelper({
        status,
        severity,
        defectStatusFilter,
        assignedToFilter,
        dateRange,
      }),
    [defectStatusFilter, assignedToFilter, dateRange],
  );

  // Build dynamic route for inspections navigation with all active filters
  const buildInspectionsRoute = useCallback(
    (status?: string): string =>
      buildInspectionsRouteHelper({ status, inspectionStatusFilter, assignedToFilter, dateRange }),
    [inspectionStatusFilter, assignedToFilter, dateRange],
  );

  const userName = getUserGreetingName(profile, user?.email, t("common.user"));

  // Calculate open totals (sum of active statuses - overdue items are already included in notStarted/active)
  const inspectionsOpen = stats ? stats.inspectionsNotStarted + stats.inspectionsActive : 0;
  const defectsOpen = stats
    ? stats.defectsNotStarted + stats.defectsActive + stats.defectsToVerify
    : 0;

  // Prüfungen Kacheln — Definitionen aus dashboardHelpers (verhaltenserhaltend, TES-848)
  const inspectionCards = useMemo(
    () =>
      buildInspectionCards({
        stats,
        inspectionsOpen,
        roles: { isQualitaetsmanager, isTester, isAdmin },
        t,
        navigate,
        routeFor: buildInspectionsRoute,
      }),
    [
      stats,
      inspectionsOpen,
      isQualitaetsmanager,
      isTester,
      isAdmin,
      navigate,
      buildInspectionsRoute,
      t,
    ],
  );

  // Mängel Kacheln — Definitionen aus dashboardHelpers (verhaltenserhaltend, TES-848)
  const defectCards = useMemo(
    () =>
      buildDefectCards({
        stats,
        defectsOpen,
        roles: { isQualitaetsmanager, isTester, isAdmin },
        t,
        navigate,
        routeFor: buildDefectsRoute,
      }),
    [stats, defectsOpen, isQualitaetsmanager, isTester, isAdmin, navigate, buildDefectsRoute, t],
  );

  const dateRangeLabel = useMemo(() => {
    if (datePreset === "all" || !dateRange?.from) return t("common.allData");
    if (!dateRange.to) return format(dateRange.from, "d. MMM yyyy", { locale: dateLocale });
    return `${format(dateRange.from, "d. MMM", { locale: dateLocale })} - ${format(dateRange.to, "d. MMM yyyy", { locale: dateLocale })}`;
  }, [dateRange, datePreset, dateLocale, t]);

  return (
    <PageContainer className="space-y-8" data-testid="dashboard-page">
      {/* Setup Validation Warning for Admins */}
      <SetupValidationWarning />
      <SecretsWarning />

      {/* Header - Industrial Management Portal (screen-04): Breadcrumb + Titel + Actions */}
      <RoleDashboardHeader
        userName={userName}
        title={t("dashboard.overview")}
        actions={<DashboardQuickActions />}
      />

      {/* TES-506: Dashboard-Reiter — Übersicht (bestehend) + Offene Prüfungen (neu). */}
      <Tabs defaultValue="overview" className="space-y-8">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview">
            {t("openInspections.overviewTab")}
          </TabsTrigger>
          {showInspections && (
            <TabsTrigger value="open-inspections" data-testid="dashboard-tab-open-inspections">
              {t("openInspections.tabLabel")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-8 mt-0">
          {/* TES-1633: In-App-BI-KPIs (aggregierte Headline-Kennzahlen, RLS-scoped) — lazy-Chunk */}
          <Suspense fallback={null}>
            <DashboardKpiSection />
          </Suspense>
          {/* Filter row (preserved data-tour anchor) */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="hidden lg:block" />
            {/* Filters */}
            <div
              data-tour="dashboard-filters"
              className="flex flex-wrap items-center gap-2 md:gap-3"
            >
              {/* Date Preset */}
              <Select
                value={datePreset}
                onValueChange={(value) => handlePresetChange(value as DatePreset)}
              >
                {/* TES-879: aria-label for WCAG select-name (icon/placeholder-only triggers) */}
                <SelectTrigger
                  className="w-[120px] md:w-[140px] text-xs md:text-sm"
                  aria-label={t("dashboard.selectPeriod")}
                >
                  <SelectValue placeholder={t("dashboard.selectPeriod")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.allData")}</SelectItem>
                  <SelectItem value="today">{t("common.today")}</SelectItem>
                  <SelectItem value="week">{t("common.thisWeek")}</SelectItem>
                  <SelectItem value="month">{t("common.thisMonth")}</SelectItem>
                  <SelectItem value="3months">{t("common.last3Months")}</SelectItem>
                  <SelectItem value="year">{t("common.lastYear")}</SelectItem>
                  <SelectItem value="custom">{t("common.custom")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Custom Date Range Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !dateRange && "text-muted-foreground",
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {dateRangeLabel}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    autoFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range);
                      setDatePreset("custom");
                    }}
                    numberOfMonths={2}
                    locale={dateLocale}
                  />
                </PopoverContent>
              </Popover>

              {/* Team Filter */}
              <TeamFilterDropdown value={teamFilter} onChange={setTeamFilter} />

              {/* Inspection Status Filter */}
              {showInspections && (
                <Select value={inspectionStatusFilter} onValueChange={setInspectionStatusFilter}>
                  <SelectTrigger
                    className="w-[130px] md:w-[180px] text-xs md:text-sm"
                    aria-label={t("dashboard.inspections")}
                  >
                    <ClipboardList className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
                    <SelectValue placeholder={t("dashboard.inspections")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("dashboard.allStatuses")}</SelectItem>
                    <SelectItem value="openAll">{t("dashboard.openAll")}</SelectItem>
                    <SelectItem value="pending">{t("dashboard.notStarted")}</SelectItem>
                    <SelectItem value="in_progress">{t("dashboard.inProgress")}</SelectItem>
                    <SelectItem value="completed">{t("dashboard.completed")}</SelectItem>
                    <SelectItem value="cancelled">{t("dashboard.cancelled")}</SelectItem>
                    <SelectItem value="overdue">{t("dashboard.overdue")}</SelectItem>
                    <SelectItem value="unassigned">{t("dashboard.unassigned")}</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Defect Status Filter */}
              <Select value={defectStatusFilter} onValueChange={setDefectStatusFilter}>
                <SelectTrigger
                  className="w-[130px] md:w-[180px] text-xs md:text-sm"
                  aria-label={t("dashboard.defects")}
                >
                  <AlertTriangle className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 shrink-0" />
                  <SelectValue placeholder={t("dashboard.defects")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dashboard.allStatuses")}</SelectItem>
                  <SelectItem value="openAll">{t("dashboard.openAll")}</SelectItem>
                  <SelectItem value="open">{t("dashboard.notStarted")}</SelectItem>
                  <SelectItem value="in_progress">{t("dashboard.inProgress")}</SelectItem>
                  <SelectItem value="resolved">{t("dashboard.toVerify")}</SelectItem>
                  <SelectItem value="closed">{t("dashboard.completed")}</SelectItem>
                  <SelectItem value="cancelled">{t("dashboard.cancelled")}</SelectItem>
                  <SelectItem value="overdue">{t("dashboard.overdue")}</SelectItem>
                  <SelectItem value="unassigned">{t("dashboard.unassigned")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Severity Filter - hidden on small screens */}
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger
                  className="hidden lg:flex w-[160px] text-sm"
                  aria-label={t("defects.severity.label")}
                >
                  <AlertTriangle className="mr-2 h-4 w-4 shrink-0" />
                  <SelectValue placeholder={t("defects.severity.label")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dashboard.allSeverities")}</SelectItem>
                  <SelectItem value="critical">{t("defects.severity.critical")}</SelectItem>
                  <SelectItem value="high">{t("defects.severity.high")}</SelectItem>
                  <SelectItem value="medium">{t("defects.severity.medium")}</SelectItem>
                  <SelectItem value="low">{t("defects.severity.low")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Assigned To Filter - hidden on small screens */}
              {canSeeAll && (
                <ProfileSelect
                  profiles={assignableProfiles}
                  value={assignedToFilter === "all" ? "" : assignedToFilter}
                  onValueChange={(value) => setAssignedToFilter(value || "all")}
                  placeholder={t("common.assignedTo")}
                  allowUnassigned
                  unassignedLabel={t("common.all")}
                  className="hidden lg:flex w-[200px] text-sm"
                />
              )}

              {/* Clear All Filters Button */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="gap-1.5 bg-gradient-to-r from-primary/20 to-info/20 border-primary/30 text-primary hover:from-primary/30 hover:to-info/30 hover:text-primary"
                >
                  <X className="h-3.5 w-3.5" />
                  {t("common.clearAllFilters")}
                </Button>
              )}

              {/* Refresh Button */}
              {/* TES-879: icon-only button needs a discernible name (WCAG button-name) */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label={t("common.refresh")}
              >
                <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              </Button>
            </div>
          </div>

          {/* Active Filter Chips - Always visible for layout stability */}
          <div className="flex flex-wrap gap-1.5 md:gap-2 items-center p-2 md:p-3 bg-muted/50 rounded-lg border min-h-[44px] md:min-h-[52px] -mt-4">
            <span className="text-sm text-muted-foreground mr-1">{t("common.filter")}:</span>

            {!hasActiveFilters && (
              <span className="text-sm text-muted-foreground">{t("common.none")}</span>
            )}

            {/* Date Range Chip */}
            {datePreset !== "all" && dateRange?.from && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-secondary/80"
                onClick={() => {
                  setDatePreset("all");
                  setDateRange(undefined);
                }}
              >
                {t("common.period")}: {format(dateRange.from, "d. MMM", { locale: dateLocale })}
                {dateRange.to &&
                  dateRange.to.getTime() !== dateRange.from.getTime() &&
                  ` - ${format(dateRange.to, "d. MMM", { locale: dateLocale })}`}
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Inspection Status Chip */}
            {showInspections && inspectionStatusFilter !== "all" && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-secondary/80"
                onClick={() => setInspectionStatusFilter("all")}
              >
                {t("dashboard.inspections")}:{" "}
                {resolveInspectionChipLabel(inspectionStatusFilter, t)}
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Defect Status Chip */}
            {defectStatusFilter !== "all" && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-secondary/80"
                onClick={() => setDefectStatusFilter("all")}
              >
                {t("dashboard.defects")}: {resolveDefectChipLabel(defectStatusFilter, t)}
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Severity Chip */}
            {severityFilter !== "all" && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-secondary/80"
                onClick={() => setSeverityFilter("all")}
              >
                {t("defects.severity.label")}: {t(`defects.severity.${severityFilter}`)}
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Assigned To Chip */}
            {assignedToFilter !== "all" && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-secondary/80"
                onClick={() => setAssignedToFilter("all")}
              >
                {t("common.assignedTo")}:{" "}
                {assignableProfiles.find((p) => p.user_id === assignedToFilter)
                  ? `${assignableProfiles.find((p) => p.user_id === assignedToFilter)?.first_name} ${assignableProfiles.find((p) => p.user_id === assignedToFilter)?.last_name}`
                  : assignedToFilter}
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Clear All Button */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
              >
                <X className="h-3 w-3 mr-1" />
                {t("common.clearAllFilters")}
              </Button>
            )}
          </div>

          {/* Show skeleton when loading initial data, otherwise show content */}
          {isLoading && !stats ? (
            <DashboardSkeleton />
          ) : (
            <>
              {/* Industrial Management Portal — 12-col Bento (Desktop screen-04) + Mobile Stack (screen-09) */}
              <DashboardBento
                recentInspections={recentInspections}
                criticalDefects={criticalDefects}
                stats={stats}
                defectsOpen={defectsOpen}
                getDisplayStatus={getDisplayStatus}
              />

              {/* Prüfungen Section - nur für Nicht-Mangelbearbeiter */}
              {showInspections && (
                <div data-tour="dashboard-inspections" className="space-y-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-primary" />
                    {t("dashboard.inspections")}
                  </h2>
                  <div className="grid container-stats-6 gap-3 sm:gap-4">
                    {inspectionCards.map((stat) => (
                      <StatCard key={stat.testId} {...stat} />
                    ))}
                  </div>
                </div>
              )}

              {/* Mängel Section */}
              <div data-tour="dashboard-defects" className="space-y-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                  {t("dashboard.defects")}
                </h2>
                <div className="grid container-stats-6 gap-3 sm:gap-4">
                  {defectCards.map((stat) => (
                    <StatCard key={stat.testId} {...stat} />
                  ))}
                </div>
              </div>

              {/* Charts Row - Prüfungen - nur für Nicht-Mangelbearbeiter */}
              {showInspections && (
                <InspectionCharts
                  inspectionTrend={inspectionTrend}
                  inspectionsByStatus={inspectionsByStatus}
                  inspectionsSimple={inspectionsSimple}
                  stats={stats}
                  showDetailedView={showDetailedView}
                  onToggleDetailedView={() => setShowDetailedView(!showDetailedView)}
                />
              )}

              {/* Charts Row - Mängel */}
              <DefectCharts
                defectTrend={defectTrend}
                defectsByStatus={defectsByStatus}
                defectsSimple={defectsSimple}
                defectsBySeverity={defectsBySeverity}
                stats={stats}
                showDetailedView={showDetailedView}
                onToggleDetailedView={() => setShowDetailedView(!showDetailedView)}
                buildDefectsRoute={buildDefectsRoute}
              />

              {/* Score Statistics */}
              <ScoreStatistics dateRange={dateRange} />

              {/* Team Statistics - Only for QM */}
              {canSeeAll && (
                <div data-tour="dashboard-team">
                  <TeamStatistics teamId={effectiveTeamFilter ?? undefined} />
                </div>
              )}

              {/* User Permissions Overview */}
              <UserPermissionsCard />

              {/* Summary Stats - Two columns for Prüfungen and Mängel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t("dashboard.totalOverview")} ({dateRangeLabel})
                  </CardTitle>
                  <CardDescription>{t("dashboard.allStatsInPeriod")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    className={cn(
                      "grid gap-6",
                      showInspections ? "container-grid-2" : "grid-cols-1",
                    )}
                  >
                    {/* Prüfungen Summary - nur für Nicht-Mangelbearbeiter */}
                    {showInspections && (
                      <div className="space-y-4">
                        <h3 className="font-semibold flex items-center gap-2 text-sm text-muted-foreground">
                          <ClipboardList className="h-4 w-4" />
                          {t("dashboard.inspections")}
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-4 rounded-lg bg-secondary/30">
                            <p className="text-2xl font-bold text-primary">
                              {stats?.totalInspections || 0}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t("common.total")}
                            </p>
                          </div>
                          <div className="text-center p-4 rounded-lg bg-secondary/30">
                            <p className="text-2xl font-bold text-success">
                              {stats?.inspectionsCompleted || 0}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t("dashboard.completed")}
                            </p>
                          </div>
                          <div className="text-center p-4 rounded-lg bg-secondary/30">
                            <p className="text-2xl font-bold text-warning">
                              {stats?.inspectionsNotStarted || 0}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t("dashboard.notStarted")}
                            </p>
                          </div>
                          <div className="text-center p-4 rounded-lg bg-secondary/30">
                            <p className="text-2xl font-bold text-destructive">
                              {stats?.inspectionsOverdue || 0}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t("dashboard.overdue")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mängel Summary */}
                    <div className="space-y-4">
                      <h3 className="font-semibold flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertTriangle className="h-4 w-4" />
                        {t("dashboard.defects")}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 rounded-lg bg-secondary/30">
                          <p className="text-2xl font-bold text-primary">
                            {stats?.totalDefects || 0}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{t("common.total")}</p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-secondary/30">
                          <p className="text-2xl font-bold text-success">
                            {stats?.defectsCompleted || 0}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("dashboard.completed")}
                          </p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-secondary/30">
                          <p className="text-2xl font-bold text-warning">
                            {stats?.defectsNotStarted || 0}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("dashboard.notStarted")}
                          </p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-secondary/30">
                          <p className="text-2xl font-bold text-destructive">
                            {stats?.defectsOverdue || 0}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("dashboard.overdue")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* TES-506: Offene Prüfungen — Übersicht offener/anstehender inspections. */}
        {showInspections && (
          <TabsContent value="open-inspections" className="mt-0">
            <OpenInspectionsPanel />
          </TabsContent>
        )}
      </Tabs>
    </PageContainer>
  );
}
